import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, User, CircleDollarSign, Users2, Folder, Hourglass } from 'lucide-react';

import { useUIStore } from '../../stores/uiStore';
import { useProfileStore } from '../../stores/profileStore';
import { ROLES } from '../../types/auth';
import { useNotificationStore } from '../../stores/notificationStore';
import { VerticalNavBar, MenuItemType } from './VerticalNavbar';
import { HorizontalNavbar } from './HorizontalNavbar';
import { useCreditStore } from '../../stores/creditStore';
import { WithCreditsModal } from '../credits/WithCreditsModal';

interface BreadCrumb {
  key: string;
  route?: string;
}

interface DashboardNavigationProps {
  activeKey?: string;
  breadCrumbs?: BreadCrumb[];
  showMobileBreadCrumbs?: boolean;
}

export const DashboardNavigation: React.FC<DashboardNavigationProps> = ({
  activeKey,
  breadCrumbs,
  showMobileBreadCrumbs = false,
}) => {
  const [isVerticalNavBar, setIsVerticalNavBar] = useState(true);

  const { t } = useTranslation('common');
  const location = useLocation();
  const { activeBreadCrumb } = useUIStore();
  const { profile } = useProfileStore();
  const { allianceNotificationsCount, quoteRequestNotificationsCount } = useNotificationStore();
  const { balance, refreshBalance } = useCreditStore();

  useEffect(() => {
    if (balance === null) {
      refreshBalance();
    }
  }, [balance, refreshBalance]);

  const navItems: MenuItemType[] = [
    {
      key: 'home',
      label: t('navBar.home'),
      icon: <Home />,
      route: '/dashboard',
    },
  ];

  if (profile?.role === ROLES.FREELANCER) {
    navItems.push(
      {
        key: 'prospects',
        label: t('navBar.prospects'),
        route: '/prospects',
        badge: quoteRequestNotificationsCount,
        icon: <User />,
      },
      {
        key: 'alliances',
        label: t('navBar.alliances'),
        route: '/alliances',
        badge: allianceNotificationsCount,
        icon: <Users2 />,
      },
      {
        key: 'quotes',
        label: t('navBar.quotes'),
        route: '/quotes',
        icon: <CircleDollarSign />,
      }
    );
  } else if (profile?.role === ROLES.RECRUITER) {
    navItems.push(
      {
        key: 'prospects',
        label: t('navBar.contacts'),
        route: '/contacts',
        icon: <User />,
      },
      {
        key: 'jobs',
        label: t('navBar.jobs'),
        route: '/jobs',
        icon: <Folder />,
      }
    );
  }

  navItems.push({
    key: 'projects',
    label: t('navBar.projects'),
    route: '/projects',
    icon: <Hourglass />,
  });

  const effectiveBreadCrumbs = breadCrumbs || [
    { key: 'breadcrumbs.dashboard', route: '/dashboard' },
  ];
  const totalBreadCrumbs = effectiveBreadCrumbs.length;

  const credits = balance || 0;

  return (
    <WithCreditsModal handleUrlParams={true}>
      {openCreditsModal => (
        <div
          className={`${showMobileBreadCrumbs ? 'block' : 'hidden'} md:block md:px-4 lg:px-32 mt-8 md:mt-4 `}
        >
          {/* Breadcrumbs */}
          <div className="flex items-center gap-1.5 mb-2 text-sm text-secondary/50">
            {effectiveBreadCrumbs.map((crumb, index) => {
              const isLast = index === totalBreadCrumbs - 1 && !activeBreadCrumb;
              return (
                <React.Fragment key={crumb.key}>
                  {crumb.route && !isLast ? (
                    <Link to={crumb.route} className="hover:text-secondary transition-colors">
                      {t(crumb.key)}
                    </Link>
                  ) : (
                    <span className={isLast ? 'font-bold text-secondary' : ''}>{t(crumb.key)}</span>
                  )}
                  {index < totalBreadCrumbs - 1 && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="flex-shrink-0 opacity-50"
                    >
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  )}
                </React.Fragment>
              );
            })}
            {activeBreadCrumb && (
              <>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="flex-shrink-0 opacity-50"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
                <span className="font-bold text-secondary">{activeBreadCrumb}</span>
              </>
            )}
          </div>

          {!isVerticalNavBar ? (
            <HorizontalNavbar
              navItems={navItems}
              activeKey={activeKey}
              pathname={location.pathname}
              credits={credits}
              onDockLeft={() => setIsVerticalNavBar(true)}
              onShowCreditsModal={openCreditsModal}
            />
          ) : (
            <VerticalNavBar
              open={isVerticalNavBar}
              menuItems={navItems}
              pathname={location.pathname}
              credits={credits}
              onDockTop={() => setIsVerticalNavBar(false)}
              onShowCreditsModal={openCreditsModal}
            />
          )}
        </div>
      )}
    </WithCreditsModal>
  );
};
